import { styled } from "@mui/material";
import { ColorSAP, FONT_FAMILY } from "../../ontego-ui";

const Styled = {};

// ─── Container (header) ─────────────────────────────────────────────────────

Styled.Container = styled("div")(({ isScrolled, isDark }) => ({
  position: isScrolled || isDark ? "fixed" : "absolute",
  width: "100%",
  top: 0,
  left: 0,
  zIndex: 1300,
  pointerEvents: "none",
}));

// ─── Nav Section (outer wrapper – provides vertical padding around pill) ────

Styled.NavSection = styled("div")(({ isScrolled, isPillHidden, isDark }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  zIndex: 2,
  pointerEvents: "auto",
  ...(isDark
    ? {
        transition: "none",
        transform: "translateY(0)",
        padding: "16px 0",
        backgroundColor: ColorSAP.primary[70],
        boxShadow: "none",
      }
    : {
        transition: "transform 0.4s ease",
        ...(isScrolled
          ? {
              transform: "translateY(0)",
              padding: "16px 0",
              backgroundColor: ColorSAP.neutral[10],
              boxShadow: "0px 1px 24px 0px rgba(0, 0, 0, 0.22)",
            }
          : isPillHidden
            ? {
                transform: "translateY(-100%)",
                padding: "28px 0 0 0",
                backgroundColor: "transparent",
                boxShadow: "none",
              }
            : {
                transform: "translateY(0)",
                padding: "28px 0 0 0",
                backgroundColor: "transparent",
                boxShadow: "none",
              }),
      }),
}));

// ─── Nav Pill (the pill shape / flat bar containing logo + menus) ───────────

Styled.NavPill = styled("div")(({ isScrolled, isPillHidden, isDark }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  boxSizing: "border-box",
  maxWidth: 1200,
  "@media (max-width: 1280px)": {
    maxWidth: "calc(100% - 80px)",
  },
  ...(isDark
    ? {
        padding: 0,
        borderRadius: 0,
        backgroundColor: "transparent",
        height: 56,
      }
    : isScrolled
      ? {
          padding: 0,
          borderRadius: 0,
          backgroundColor: "transparent",
          height: 56,
        }
      : {
          padding: "18px 48px",
          borderRadius: 48,
          backgroundColor: ColorSAP.neutral[10],
        }),
}));

Styled.Logo = styled("div")(() => ({
  cursor: "pointer",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  "& svg": {
    width: "106px !important",
    height: "44px !important",
  },
}));

// ─── Menus (right side) ─────────────────────────────────────────────────────

Styled.Menus = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: 40,
}));

Styled.MenuText = styled("span")(({ isDark }) => ({
  fontFamily: FONT_FAMILY.PLUS_JAKARTA_SANS_MEDIUM,
  fontSize: 18,
  lineHeight: "22px",
  color: isDark ? ColorSAP.neutral[0] : ColorSAP.neutral[100],
  cursor: "pointer",
  padding: "4px 0",
  userSelect: "none",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: 0,
    height: 2,
    backgroundColor: isDark ? ColorSAP.neutral[0] : ColorSAP.neutral[100],
    transition: "width 0.3s ease",
  },
  "&:hover::after": {
    width: "100%",
  },
}));

Styled.ResourcesWrapper = styled("div")(({ isDark }) => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  padding: "4px 0",
  userSelect: "none",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: 0,
    height: 2,
    backgroundColor: isDark ? ColorSAP.neutral[0] : ColorSAP.neutral[100],
    transition: "width 0.3s ease",
  },
  "&:hover::after": {
    width: "100%",
  },
}));

Styled.ResourcesText = styled("span")(({ isDark }) => ({
  fontFamily: FONT_FAMILY.PLUS_JAKARTA_SANS_MEDIUM,
  fontSize: 18,
  lineHeight: "22px",
  color: isDark ? ColorSAP.neutral[0] : ColorSAP.neutral[100],
}));

Styled.ArrowIcon = styled("div")(({ isOpen, isDark }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  transition: "transform 0.15s linear",
  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
  "& svg": {
    width: "20px !important",
    height: "20px !important",
    color: isDark ? ColorSAP.neutral[0] : ColorSAP.neutral[100],
  },
}));

// ─── Language Switcher ──────────────────────────────────────────────────────

Styled.LangSwitcher = styled("div")(() => ({
  display: "flex",
  alignItems: "flex-start",
}));

Styled.LangButton = styled("div")(({ isActive, position, isDark }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 41,
  padding: "4px 8px",
  boxSizing: "border-box",
  cursor: "pointer",
  border: `1px solid ${isDark ? ColorSAP.neutral[0] : ColorSAP.primary[70]}`,
  backgroundColor: isDark
    ? isActive
      ? ColorSAP.neutral[0]
      : "transparent"
    : isActive
      ? ColorSAP.primary[70]
      : "transparent",
  borderTopLeftRadius: position === "left" ? 8 : 0,
  borderBottomLeftRadius: position === "left" ? 8 : 0,
  borderTopRightRadius: position === "right" ? 8 : 0,
  borderBottomRightRadius: position === "right" ? 8 : 0,
  marginRight: position !== "right" ? -1 : 0,
  fontFamily: isActive
    ? FONT_FAMILY.PLUS_JAKARTA_SANS_BOLD_ITALIC
    : FONT_FAMILY.PLUS_JAKARTA_SANS_MEDIUM,
  fontSize: 18,
  lineHeight: "22px",
  color: isDark
    ? isActive
      ? ColorSAP.primary[70]
      : ColorSAP.neutral[0]
    : isActive
      ? ColorSAP.neutral[0]
      : ColorSAP.neutral[100],
  transition: "all 0.3s ease-in-out",
}));

// ─── Logout Button ──────────────────────────────────────────────────────────

Styled.LogoutButton = styled("div")(({ isDark }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  backgroundColor: isDark ? ColorSAP.neutral[0] : ColorSAP.neutral[90],
  borderRadius: 100,
  cursor: "pointer",
  transition: "background 300ms ease",
  "&:hover": {
    backgroundColor: isDark ? ColorSAP.neutral[15] : ColorSAP.neutral[80],
  },
}));

Styled.LogoutText = styled("span")(({ isDark }) => ({
  fontFamily: FONT_FAMILY.PLUS_JAKARTA_SANS_BOLD,
  fontSize: 12,
  lineHeight: "14px",
  color: isDark ? ColorSAP.neutral[100] : ColorSAP.neutral[0],
  whiteSpace: "nowrap",
}));

Styled.LogoutArrow = styled("div")(({ isDark }) => ({
  width: 28,
  height: 28,
  borderRadius: 40,
  backgroundColor: isDark ? ColorSAP.neutral[15] : ColorSAP.neutral[70],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    width: "14px !important",
    height: "14px !important",
    color: isDark ? ColorSAP.neutral[100] : ColorSAP.neutral[0],
  },
}));

// ─── Mega Dropdown ──────────────────────────────────────────────────────────

Styled.DropdownWrapper = styled("div")(({ isVisible }) => ({
  opacity: isVisible ? 1 : 0,
  visibility: isVisible ? "visible" : "hidden",
  transform: isVisible ? "translateY(0)" : "translateY(-20px)",
  transition: "opacity 0.3s ease, visibility 0.3s ease, transform 0.3s ease",
  position: "relative",
  zIndex: 1,
  pointerEvents: "none",
}));

Styled.HoverBridge = styled("div")(() => ({
  height: 16,
  width: "100%",
  pointerEvents: "auto",
}));

Styled.MegaDropdown = styled("div")(({ isScrolled }) => ({
  backgroundColor: ColorSAP.neutral[10],
  display: "flex",
  gap: 24,
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  boxShadow: "0px 16px 24px 0px rgba(0, 0, 0, 0.14)",
  boxSizing: "border-box",
  pointerEvents: "auto",
  ...(isScrolled
    ? {
        padding: "48px 120px",
        borderRadius: 0,
        "@media (max-width: 1440px)": {
          padding: "48px 40px",
        },
      }
    : {
        padding: 48,
        borderRadius: 24,
        maxWidth: 1200,
        margin: "0 auto",
        "@media (max-width: 1280px)": {
          maxWidth: "calc(100% - 80px)",
        },
      }),
}));

// ─── Resource Card ──────────────────────────────────────────────────────────

Styled.Card = styled("a")(({ isScrolled }) => ({
  display: "flex",
  gap: "40px",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: 24,
  borderRadius: 16,
  border: `2px solid ${ColorSAP.neutral[15]}`,
  background: `linear-gradient(104.74deg, ${ColorSAP.neutral[10]} 1.36%, ${ColorSAP.neutral[15]} 105.87%)`,
  overflow: "hidden",
  textDecoration: "none",
  cursor: "pointer",
  boxSizing: "border-box",
  transition: "border-color 0.2s ease, background 0.2s ease",
  ...(isScrolled
    ? { flex: "1 0 0", minWidth: 0, minHeight: 1 }
    : { width: 258, flexShrink: 0 }),
  "&:hover": {
    borderColor: ColorSAP.neutral[20],
    background: `linear-gradient(104.74deg, ${ColorSAP.neutral[10]} 1.36%, ${ColorSAP.neutral[20]} 105.87%)`,
  },
  "&:active": {
    background: `linear-gradient(${ColorSAP.primary[10]}, ${ColorSAP.primary[10]}) padding-box, linear-gradient(to bottom, ${ColorSAP.primary[30]}, ${ColorSAP.primary[50]}) border-box`,
    borderColor: "transparent",
  },
  "&:active .card-icon svg .icon-accent": {
    fill: `${ColorSAP.primary[40]} !important`,
  },
  "&:active .card-icon svg .icon-main": {
    fill: `${ColorSAP.primary[60]} !important`,
  },
}));

Styled.CardIcon = styled("div")(() => ({
  width: 72,
  height: 72,
  flexShrink: 0,
  "& svg": {
    width: "72px !important",
    height: "72px !important",
  },
  "& svg .icon-accent": {
    transition: "fill 0.2s ease",
  },
  "& svg .icon-main": {
    transition: "fill 0.2s ease",
  },
}));

Styled.CardContent = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 24,
  width: "100%",
  flex: 1,
}));

Styled.CardText = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
}));

Styled.CardLearnMore = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginTop: "auto",
}));

Styled.CardLearnMoreText = styled("span")(() => ({
  fontFamily: FONT_FAMILY.PLUS_JAKARTA_SANS_SEMIBOLD,
  fontSize: 14,
  color: ColorSAP.neutral[100],
}));

Styled.CardLearnMoreArrow = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  "& svg": {
    width: "14px !important",
    height: "14px !important",
    color: ColorSAP.neutral[100],
  },
}));

export default Styled;
